import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Button,
  TextField,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { criarPix } from '../../../API/service';
import PixPaymentStatus from './PixPaymentStatus';
import { jwtDecode } from 'jwt-decode';
import { getClientSignals } from '../../../utils/clientSignals';
import { trackMetaEvent, trackMetaPurchase } from '../../../utils/metaPixel';
import { registrarSalesEvent } from '../../../utils/salesEvents';

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const isValidCpf = (value) => {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const calculateDigit = (base) => {
    let factor = base.length + 1;
    const total = base
      .split('')
      .reduce((sum, digit) => sum + Number(digit) * factor--, 0);
    const rest = (total * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  return (
    calculateDigit(cpf.slice(0, 9)) === Number(cpf[9]) &&
    calculateDigit(cpf.slice(0, 10)) === Number(cpf[10])
  );
};

const PixPayment = ({
  onCancel,
  cancelLabel = '',
  valorPagamento,
  userId,
  initialBuyer = {},
  accessDurationLabel = '12 meses',
  onStageChange,
}) => {
  const navigate = useNavigate();

  // Estados
  const [firstName, setFirstName] = useState(initialBuyer.fullName || '');
  const [email, setEmail] = useState(initialBuyer.email || '');
  const [cpf, setCpf] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [transactionAmount, setTransactionAmount] = useState(
    Number(valorPagamento) > 0 ? Number(valorPagamento) : null
  );
  const [qrCodeBase64, setQrCodeBase64] = useState('');
  const [copiaCola, setCopiaCola] = useState('');
  const trackedCheckoutPaymentIdRef = useRef(null);
  const formStartedRef = useRef(false);

  useEffect(() => {
    setFirstName((current) => current || initialBuyer.fullName || '');
    setEmail((current) => current || initialBuyer.email || '');
  }, [initialBuyer.email, initialBuyer.fullName]);

  useEffect(() => {
    if (Number(valorPagamento) > 0) {
      setTransactionAmount(Number(valorPagamento));
    }
  }, [valorPagamento]);

  const markFormStarted = (firstField) => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    onStageChange?.('form_started');
    registrarSalesEvent(userId, 'payment_form_started', {
      source: 'payment_form',
      metadata: { method: 'pix', first_field: firstField },
    });
  };

  const showValidationError = (field, code, message) => {
    setError(message);
    registrarSalesEvent(userId, 'payment_validation_error', {
      source: 'payment_form',
      metadata: { method: 'pix', field, code },
    });
  };

  // Funções utilitárias
  const formatarCPF = (value) =>
    onlyDigits(value)
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{2})$/, '$1-$2');

  const validarEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Envio do pagamento
  const handlePagamento = async () => {
    markFormStarted('submit');

    if (!firstName || !email || !cpf) {
      const missingField = !firstName ? 'full_name' : !email ? 'email' : 'cpf';
      showValidationError(
        missingField,
        'required',
        'Por favor, preencha todos os campos obrigatórios.'
      );
      return;
    }
    if (!validarEmail(email)) {
      showValidationError('email', 'invalid_email', 'Email inválido.');
      return;
    }
    if (!isValidCpf(cpf)) {
      showValidationError(
        'cpf',
        'invalid_cpf',
        'Informe um CPF válido para gerar o Pix.'
      );
      return;
    }
    if (!(Number(transactionAmount) > 0)) {
      showValidationError(
        'amount',
        'amount_unavailable',
        'Não foi possível carregar o valor. Volte e tente novamente.'
      );
      return;
    }

    setError('');
    setLoading(true);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Erro de autenticação. Faça login novamente.');
      setLoading(false);
      return;
    }

    const decodedToken = jwtDecode(token);
    const decodedUserId = decodedToken.userId;

    const body = {
      userId: decodedUserId,
      transaction_amount: transactionAmount,
      description: 'Assinatura Future Trade - ASAAS',
      email,
      first_name: firstName,
      identificationNumber: onlyDigits(cpf),
      clientSignals: getClientSignals(),
    };

    try {
      const response = await criarPix(body);

      if (response.paymentId && response.qrCode && response.copiaCola) {
        onStageChange?.('charge_created');
        registrarSalesEvent(userId, 'payment_submitted', {
          source: 'payment_form',
          metadata: {
            method: 'pix',
            payment_id: response.paymentId,
            amount: Number(response.valor) || Number(transactionAmount),
          },
        });
        if (trackedCheckoutPaymentIdRef.current !== response.paymentId) {
          trackMetaEvent(
            "InitiateCheckout",
            {
              content_name: "Assinatura Future Trade",
              content_category: "subscription",
              content_ids: ["future-trade-annual"],
              content_type: "product",
              value: Number(response.valor) || Number(transactionAmount) || 249,
              currency: "BRL",
              num_items: 1,
            },
            `ic:${response.paymentId}`
          );
          trackedCheckoutPaymentIdRef.current = response.paymentId;
        }
        setTransactionId(response.paymentId);
        setTransactionAmount(Number(response.valor) || Number(transactionAmount));
        setQrCodeBase64(response.qrCode);
        setCopiaCola(response.copiaCola);
      } else {
        setError('Erro: retorno inesperado do servidor.');
        registrarSalesEvent(userId, 'payment_provider_error', {
          source: 'payment_form',
          metadata: { method: 'pix', code: 'unexpected_provider_response' },
        });
      }
    } catch (err) {
      console.error('Erro ao criar PIX ASAAS:', err);
      registrarSalesEvent(userId, 'payment_provider_error', {
        source: 'payment_form',
        metadata: {
          method: 'pix',
          http_status: err.response?.status || null,
          code: err.response?.data?.code || 'pix_creation_failed',
        },
      });
      setError(
        err.response?.data?.mensagem ||
          err.response?.data?.error ||
          'Erro ao gerar o QR Code. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    registrarSalesEvent(userId, 'pix_code_copied', {
      source: 'payment_form',
      metadata: { stage: 'charge_created' },
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGerarNovoPix = () => {
    setTransactionId(null);
    setQrCodeBase64('');
    setCopiaCola('');
    setCopied(false);
    setError('');
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, rgba(14,22,32,0.96) 0%, rgba(9,15,24,0.98) 100%)',
        color: '#F1F5FA',
        borderRadius: 3,
        p: { xs: 2.5, sm: 3.2 },
        textAlign: 'left',
        maxWidth: 500,
        mx: 'auto',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 20px 48px rgba(0,0,0,0.35)',
      }}
    >
      {!qrCodeBase64 ? (
        <>
          <Chip
            label="Pagamento instantâneo"
            sx={{
              mb: 1.4,
              color: '#D0F6DE',
              bgcolor: 'rgba(61, 181, 104, 0.18)',
              border: '1px solid rgba(61,181,104,0.42)',
              fontWeight: 700,
            }}
          />
          <Typography variant="h5" sx={{ mb: 0.7, fontWeight: 800, color: '#F6FBFF' }}>
            Pagar com Pix
          </Typography>
          <Typography sx={{ mb: 2.1, color: 'rgba(241,245,250,0.75)' }}>
            Preencha os dados para gerar seu QR Code com segurança.
          </Typography>

          <TextField
            label="Nome Completo"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onFocus={() => markFormStarted('full_name')}
            fullWidth
            margin="dense"
            variant="filled"
            InputProps={{ sx: { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff', borderRadius: 2 } }}
          />

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => markFormStarted('email')}
            fullWidth
            margin="dense"
            variant="filled"
            InputProps={{ sx: { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff', borderRadius: 2 } }}
          />

          <TextField
            label="CPF"
            value={cpf}
            onChange={(e) => setCpf(formatarCPF(e.target.value))}
            onFocus={() => markFormStarted('cpf')}
            placeholder="000.000.000-00"
            fullWidth
            margin="dense"
            variant="filled"
            InputProps={{ sx: { backgroundColor: 'rgba(255,255,255,0.06)', color: '#fff', borderRadius: 2 } }}
          />

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={handlePagamento}
            disabled={loading || !(Number(transactionAmount) > 0)}
            sx={{
              mt: 2.2,
              background: 'linear-gradient(90deg, #37B85B 0%, #5DD06D 100%)',
              color: '#07240F',
              fontWeight: 800,
              borderRadius: '10px',
              width: '100%',
              py: 1.15,
              textTransform: 'none',
              '&:hover': { background: 'linear-gradient(90deg, #41C968 0%, #70DC7D 100%)' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Gerar QR Code Pix'}
          </Button>

          {onCancel ? (
            <Button
              variant="text"
              onClick={onCancel}
              sx={{
                mt: 1.4,
                color: '#9EC5FE',
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              {cancelLabel || 'Voltar'}
            </Button>
          ) : null}
        </>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#F6FBFF', mb: 0.4 }}>
            QR Code Pix gerado
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.4, color: '#D9E9F8' }}>
            Future Trade
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mb: 2, color: 'rgba(241,245,250,0.75)' }}>
            Pague {transactionAmount.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })} via Pix.
            <br />
            Após a confirmação, seu acesso será válido por {accessDurationLabel}.
          </Typography>

          {/* ✅ Exibição correta do QR Code */}
          <Box display="flex" justifyContent="center" mb={2}>
            <img
              src={`data:image/png;base64,${qrCodeBase64}`}
              alt="QR Code ASAAS"
              style={{
                width: '220px',
                borderRadius: '12px',
                boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
              }}
            />
          </Box>

          {copiaCola && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Código Pix (Copia e Cola):
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  p: 1,
                  maxHeight: 90,
                  overflowY: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ wordBreak: 'break-word', maxWidth: '80%', color: 'rgba(241,245,250,0.86)' }}
                >
                  {copiaCola}
                </Typography>
                <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                  <IconButton onClick={() => handleCopy(copiaCola)}>
                    <ContentCopyIcon sx={{ color: '#9EC5FE' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}

          {transactionId && (
            <PixPaymentStatus
              transactionId={transactionId}
              onPaymentSuccess={() => {
                onStageChange?.('completed');
                registrarSalesEvent(userId, 'payment_completed', {
                  source: 'payment_form',
                  metadata: {
                    method: 'pix',
                    payment_id: transactionId,
                    amount: transactionAmount,
                  },
                  keepalive: true,
                });
                trackMetaPurchase({
                  paymentId: transactionId,
                  value: transactionAmount,
                });
                navigate('/pagamento/sucesso', {
                  state: {
                    paymentDetails: {
                      paymentId: transactionId,
                      amount: transactionAmount,
                      method: 'Pix',
                      purchaseTracked: true,
                    },
                  },
                });
              }}
            />
          )}

          <Button
            variant="outlined"
            onClick={handleGerarNovoPix}
            sx={{ mt: 2, color: '#9EC5FE', borderColor: 'rgba(158,197,254,0.55)', textTransform: 'none' }}
          >
            Gerar um novo Pix
          </Button>

          <Divider sx={{ my: 3, backgroundColor: 'rgba(255,255,255,0.12)' }} />

          <Box textAlign="left">
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#DDEBFA' }}>
              Como pagar:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(241,245,250,0.86)' }}>
              1. Abra o app do banco e escolha pagamento via Pix.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(241,245,250,0.86)' }}>
              2. Escaneie o QR Code ou use o código Copia e Cola.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(241,245,250,0.86)' }}>
              3. A confirmação é automática após a compensação.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PixPayment;
